import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

export interface ExportColumn {
  header: string;
  accessor: string | ((row: any) => string);
  width?: number;
}

export interface ExportOptions {
  filename: string;
  title?: string;
  subtitle?: string;
  columns: ExportColumn[];
  data: any[];
}

// Get value from row using accessor
function getValue(row: any, accessor: string | ((row: any) => string)): string {
  if (typeof accessor === 'function') {
    return accessor(row) || '';
  }
  const keys = accessor.split('.');
  let value = row;
  for (const key of keys) {
    value = value?.[key];
  }
  return value?.toString() || '';
}

// Export to CSV
export function exportToCSV(options: ExportOptions): void {
  const { filename, columns, data } = options;
  
  // Create header row
  const headers = columns.map(col => col.header);
  
  // Create data rows
  const rows = data.map(row => 
    columns.map(col => getValue(row, col.accessor))
  );
  
  // Combine headers and rows
  const csvContent = [
    headers.join(','),
    ...rows.map(row => row.map(cell => `"${cell.replace(/"/g, '""')}"`).join(','))
  ].join('\n');
  
  // Create and download file
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  saveAs(blob, `${filename}.csv`);
}

// Export to Excel
export function exportToExcel(options: ExportOptions): void {
  const { filename, title, columns, data } = options;
  
  // Create worksheet data
  const wsData: any[][] = [];
  
  // Add title if provided
  if (title) {
    wsData.push([title]);
    wsData.push([]);
  }
  
  // Add headers
  wsData.push(columns.map(col => col.header));
  
  // Add data rows
  data.forEach(row => {
    wsData.push(columns.map(col => getValue(row, col.accessor)));
  });
  
  // Create worksheet
  const ws = XLSX.utils.aoa_to_sheet(wsData);
  
  // Set column widths
  ws['!cols'] = columns.map(col => ({ wch: col.width || 15 }));
  
  // Create workbook
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
  
  // Generate and save file
  const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  saveAs(blob, `${filename}.xlsx`);
}

// Export to PDF
export function exportToPDF(options: ExportOptions): void {
  const { filename, title, subtitle, columns, data } = options;
  
  // Create PDF document
  const doc = new jsPDF();
  
  let yPos = 15;
  
  // Add title
  if (title) {
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text(title, 14, yPos);
    yPos += 8;
  }
  
  // Add subtitle
  if (subtitle) {
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(subtitle, 14, yPos);
    yPos += 8;
  }
  
  // Add table
  const headers = columns.map(col => col.header);
  const rows = data.map(row => 
    columns.map(col => getValue(row, col.accessor))
  );
  
  autoTable(doc, {
    head: [headers],
    body: rows,
    startY: yPos + 2,
    styles: {
      fontSize: 9,
      cellPadding: 3,
    },
    headStyles: {
      fillColor: [59, 130, 246],
      textColor: 255,
      fontStyle: 'bold',
    },
    alternateRowStyles: {
      fillColor: [245, 247, 250],
    },
  });
  
  // Add footer with date
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.text(
      `Generated on ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()} - Page ${i} of ${pageCount}`,
      14,
      doc.internal.pageSize.height - 10
    );
  }
  
  // Save PDF
  doc.save(`${filename}.pdf`);
}
